import { Component, Input, OnInit } from '@angular/core';
import { CustomerEntryPass } from '../../../../../../../../../models/entry-pass';
import { CustomerSearchResult } from '../../../../../../../../../models/customer';
import { Subject } from 'rxjs';
import { ListResponseCountOptional } from '../../../../../../../../../models/list-response';
import { dateStringFromISO8601, getISO8601DateStringFromMoment, propExists } from '../../../../../../../../../utils';
import { AccountService } from '../../../../../../../../../services/account/account.service';
import { CustomerService } from '../../../../../../../../../services/customer/customer.service';
import { OrganizationConfigService } from '../../../../../../../../../services/organization-config/organization-config.service';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { debounceTime } from 'rxjs/operators';
import * as moment from 'moment';


@Component({
  selector: 'app-transfer-customer-entry-pass-modal',
  templateUrl: './transfer-customer-entry-pass-modal.component.html',
  styleUrls: ['./transfer-customer-entry-pass-modal.component.css']
})
export class TransferCustomerEntryPassModalComponent implements OnInit {

  @Input() entryPass: CustomerEntryPass;
  @Input() savedCallback: (successMessage: string) => undefined;

  public selectedCustomer: CustomerSearchResult | null = null;

  public loadingSubmit = false;

  public serverError: number = null;

  public errors: any = {};
  public errorMessage = '';

  public loadingCustomers = false;
  public search = '';
  public customerFiltersChangedSubject = new Subject<null>();
  public customerList: ListResponseCountOptional<CustomerSearchResult> = null;

  // Create instance references to lookup functions and enums so they may be
  // called in the template.
  public dateStringFromISO8601 = dateStringFromISO8601;

  // The number of customer list requests. Used to ensure that only the most
  // recent request is displayed, in the event of concurrent requests (due to,
  // e.g., filtering).
  private customerListRequestCount = 0;

  private recentActivityFilter: string | undefined = undefined;

  constructor(
    private accountService: AccountService,
    private customerService: CustomerService,
    public orgConfig: OrganizationConfigService,
    public activeModal: NgbActiveModal,
    public orgConfigService: OrganizationConfigService,
  ) {}

  ngOnInit() {
    if (!this.accountService.getMyAccount().can_manage_members) {
      return;
    }

    // Handle changes to the customer filters by reloading the customers,
    // subject to debounce.
    this.customerFiltersChangedSubject.pipe(
      // Avoid rapid requests by waiting 300ms before applying the filters.
      debounceTime(300)
    ).subscribe(() => {
      this.loadCustomers();
    });
  }

  /**
   * Handle a change in the customer filters. The list of customers will be
   * filtered according to the new values.
   */
  public handleCustomerFilterChange() {
    this.loadingCustomers = true;

    this.selectedCustomer = null;

    const orgConfig = this.orgConfigService.getOrganizationConfig();
    const mustApplyRecentActivityFilter = (
      orgConfig &&
      orgConfig.customer_search_recent_activity_window_months
    );
    if (mustApplyRecentActivityFilter) {
      this.recentActivityFilter = getISO8601DateStringFromMoment(
        moment().subtract(orgConfig.customer_search_recent_activity_window_months, 'months')
      );
    }

    this.customerFiltersChangedSubject.next();
  }

  /**
   * Load the page of customers corresponding to the current page and filter
   * selections.
   */
  public loadCustomers() {
    this.loadingCustomers = true;

    // Increment the request count and save a local copy.
    this.customerListRequestCount++;
    const thisRequestCount = this.customerListRequestCount;

    if (!this.hasSearchInput()) {
      this.customerList = null;
      this.loadingCustomers = false;
      return;
    }

    this.customerService
      .searchCustomers(
        1,
        200,
        {
          search: this.search,
          last_activity_timestamp_min: this.recentActivityFilter,
        },
        true
      )
      .then(response => {
        // Only update the customer list if there has not been a more recent
        // request initiated.
        if (thisRequestCount === this.customerListRequestCount) {
          this.customerList = response;
        }
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => {
        if (thisRequestCount === this.customerListRequestCount) {
          this.loadingCustomers = false;
        }
      });
  }

  public handleSelectedCustomer(customer: CustomerSearchResult) {
    this.selectedCustomer = customer;
    this.search = customer.first_name + ' ' + customer.last_name;
    this.customerList = null;
  }

  /**
   * Whether a non-zero number of customer results have been loaded.
   */
  public customerResultsPresent(): boolean {
    return (
      this.customerList &&
      this.customerList.results &&
      (this.filteredCustomerResults().length > 0)
    );
  }

  public filteredCustomerResults(): CustomerSearchResult[] {
    return this.customerList.results.filter(
      (customer) => (this.entryPass.owner_id !== customer.id)
    );
  }

  public handleSave() {
    if (this.selectedCustomer === null) { return; }

    this.loadingSubmit = true;
    this.errors = {};
    this.errorMessage = '';

    this.customerService
      .transferEntryPass(this.entryPass.id, this.selectedCustomer.id)
      .then(() => {
        this.savedCallback('Entry pass transferred successfully.');
        this.activeModal.close();
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.errorMessage = 'You do not have permission to transfer this pass.';
        } else if (reason.status === 400) {
          this.errorMessage = 'Please correct the errors above and try again.';
          this.errors = reason.error;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingSubmit = false; });
  }

  public hasSearchInput() {
    return this.search.trim().length >= 3;
  }

  public hasRecentActivityFilter(): boolean {
    return propExists(this.recentActivityFilter);
  }

  public handleExpandSearch() {
    this.recentActivityFilter = undefined;
    this.loadingCustomers = true;
    this.customerFiltersChangedSubject.next();
  }
}
